import React, { useMemo, useState } from 'react';
import { LinhaAlerta, PainelAlertas } from '../../../interfaces/PortalCotacoes';
import Paginacao from '../../../components/paginacao/Paginacao';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * Aba "Entregas" da configuração de Cotações (at-73).
 *
 * As **quatro** situações de um pedido em aberto, uma por sub-aba: previstas para hoje,
 * em atraso, próximas e sem data. Até 11/08/2026 a tela só mostrava as duas primeiras —
 * o backend nem devolvia as outras —, então entrega marcada para amanhã era invisível.
 *
 * Uma tabela só, alimentada por configuração: quatro blocos copiados divergiriam na
 * primeira mudança de coluna. A coluna **Atraso** aparece apenas na aba de atraso, onde
 * é o dado que importa.
 *
 * Paginação **no cliente**, ao contrário de Fornecedores: `GET /alertas/painel` devolve
 * as quatro listas numa chamada só e o volume é limitado por construção (pedidos
 * `ENVIADO` não recebidos, com o atraso limitado por `ALERTA_ATRASO_MAX_DIAS`).
 */

const POR_PAGINA = 10;

/**
 * Célula de texto longo (nome de fornecedor, contato, lista de itens): UMA linha por
 * regra do Victor (12/08/2026) — célula de tabela nunca quebra em duas; o excedente vira
 * reticências e o `title` da célula mostra o valor inteiro no hover.
 */
const UMA_LINHA: React.CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'block',
};

type AbaId = 'hoje' | 'atraso' | 'proximas' | 'semData';

interface EntregasSectionProps {
  alertas: PainelAlertas | null;
  /** A carga do painel falhou — nenhuma lista vazia pode ser lida como "não há". */
  erro: boolean;
}

const formatarData = (iso: string | null): string => {
  if (!iso) return 'sem data';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'sem data';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const EntregasSection: React.FC<EntregasSectionProps> = ({ alertas, erro }) => {
  const [aba, setAba] = useState<AbaId>('hoje');
  const [pagina, setPagina] = useState<Record<AbaId, number>>({
    hoje: 1,
    atraso: 1,
    proximas: 1,
    semData: 1,
  });

  const abas = useMemo(
    () => [
      {
        id: 'hoje' as AbaId,
        rotulo: 'Previstas para hoje',
        titulo: 'Entregas previstas para hoje',
        contagem: (n: number) => `${n} pedido(s) com entrega prevista para hoje`,
        vazio: 'Nenhuma entrega prevista para hoje.',
        erro: 'Não foi possível verificar as entregas de hoje.',
        lista: alertas?.entregasHoje ?? [],
      },
      {
        id: 'atraso' as AbaId,
        rotulo: 'Em atraso',
        titulo: 'Em atraso',
        contagem: (n: number) => `${n} pedido(s) em atraso`,
        vazio: 'Nenhum pedido em atraso.',
        erro: 'Não foi possível verificar os pedidos em atraso.',
        lista: alertas?.emAtraso ?? [],
      },
      {
        id: 'proximas' as AbaId,
        rotulo: 'Próximas',
        titulo: 'Próximas entregas',
        contagem: (n: number) => `${n} pedido(s) com entrega marcada para os próximos dias`,
        vazio: 'Nenhuma entrega marcada para os próximos dias.',
        erro: 'Não foi possível verificar as próximas entregas.',
        lista: alertas?.proximas ?? [],
      },
      {
        id: 'semData' as AbaId,
        rotulo: 'Sem data',
        titulo: 'Sem data de entrega',
        contagem: (n: number) => `${n} pedido(s) sem previsão de entrega informada`,
        vazio: 'Nenhum pedido sem previsão de entrega.',
        erro: 'Não foi possível verificar os pedidos sem previsão.',
        lista: alertas?.semData ?? [],
      },
    ],
    [alertas],
  );

  const ativa = abas.find((a) => a.id === aba)!;
  const totalPaginas = Math.max(1, Math.ceil(ativa.lista.length / POR_PAGINA));
  // Recarregar o painel pode encurtar a lista (pedido recebido): página além do fim
  // mostraria tabela vazia sem explicação.
  const paginaValida = Math.min(pagina[aba], totalPaginas);
  const naTela = ativa.lista.slice((paginaValida - 1) * POR_PAGINA, paginaValida * POR_PAGINA);

  const mostraAtraso = aba === 'atraso';
  const colunas = mostraAtraso
    ? '2.4fr 1fr 0.9fr 1.2fr 0.9fr 1.8fr 1.8fr'
    : '2.6fr 1fr 1.2fr 0.9fr 2fr 2fr';
  const totalColunas = mostraAtraso ? 7 : 6;

  const renderLinha = (e: LinhaAlerta) => (
    <div key={e.pedidoId} className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
      <div className="Item">
        <div className="Data" title={e.fornecedorNome} style={{ ...UMA_LINHA, fontWeight: 600 }}>
          {e.fornecedorNome}
        </div>
      </div>
      <div className="Item">
        <div className="Data" style={{ color: e.previsao ? 'var(--tema-texto, #1a1c1b)' : 'var(--tema-texto-4, #939393)' }}>
          {formatarData(e.previsao)}
        </div>
      </div>
      {mostraAtraso && (
        <div className="Item">
          <div className="Data" style={{ color: 'var(--tema-texto-vermelho, #b42318)', fontWeight: 600 }}>
            {e.diasAtraso} dia(s)
          </div>
        </div>
      )}
      <div className="Item">
        <div className="Data">{e.cotacaoNumero}</div>
      </div>
      <div className="Item">
        <div className="Data">{e.placa ?? '—'}</div>
      </div>
      <div className="Item">
        <div className="Data" title={e.contato ?? undefined} style={UMA_LINHA}>
          {e.contato ?? 'contato não cadastrado'}
        </div>
      </div>
      <div className="Item">
        <div className="Data" title={e.itens.join('; ') || undefined} style={UMA_LINHA}>
          {e.itens.length > 0 ? e.itens.join('; ') : '—'}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {erro && (
        <p className="erro-de-tela">
          Não foi possível carregar o painel de entregas. A ausência de itens nas abas abaixo não significa que
          não há entregas nem atrasos.
        </p>
      )}

      {/*
        Abas no padrão da Recepção (`recepcao-tabs`), que é a aba de CONTEÚDO do sistema;
        `admin-tab-btn` é a barra de módulo, um nível acima. A contagem no rótulo existe
        porque, com as listas separadas, sem ela seria preciso clicar em cada aba para
        descobrir se há atraso.
      */}
      <div className="recepcao-tabs">
        {abas.map((a) => (
          <button
            key={a.id}
            type="button"
            className={`recepcao-tab ${aba === a.id ? 'active' : ''}`}
            onClick={() => setAba(a.id)}
          >
            {a.rotulo}
            {a.lista.length > 0 ? ` (${a.lista.length})` : ''}
          </button>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-card-title">{ativa.titulo}</div>

        {ativa.lista.length === 0 ? (
          <p className="admin-hint" style={{ marginTop: 0 }}>
            {erro ? ativa.erro : ativa.vazio}
          </p>
        ) : (
          <>
            <p className="admin-hint" style={{ marginTop: 0 }}>
              {ativa.contagem(ativa.lista.length)}
              {totalPaginas > 1 ? ` · página ${paginaValida} de ${totalPaginas}` : ''}
            </p>

            <div className="TabelaScroll" style={{ width: '100%', boxSizing: 'border-box' }}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: colunas,
                  width: '100%',
                  minWidth: mostraAtraso ? '1060px' : '980px',
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      <div className="Opcoes">Fornecedor</div>
                      <div className="Opcoes">Entrega</div>
                      {mostraAtraso && <div className="Opcoes">Atraso</div>}
                      <div className="Opcoes">Cotação</div>
                      <div className="Opcoes">Placa</div>
                      <div className="Opcoes">Contato</div>
                      <div className="Opcoes">Itens</div>
                    </div>
                  </div>
                </div>

                {naTela.length === 0 ? (
                  <div className="Tabela" style={{ paddingLeft: 30, paddingRight: 30 }}>
                    <div
                      className="Item"
                      style={{ gridColumn: `1 / span ${totalColunas}`, justifyContent: 'center', padding: '40px 0' }}
                    >
                      <div className="Data" style={{ textAlign: 'center' }}>Nenhum pedido nesta página.</div>
                    </div>
                  </div>
                ) : (
                  naTela.map(renderLinha)
                )}
              </div>
            </div>

            {totalPaginas > 1 && (
              <Paginacao
                paginaAtual={paginaValida}
                totalPaginas={totalPaginas}
                onMudarPagina={(p) => setPagina((atual) => ({ ...atual, [aba]: p }))}
              />
            )}
          </>
        )}
      </div>

      {/* O e-mail diário resume hoje + atraso: não pertence a nenhuma aba em particular. */}
      <p className="admin-hint">
        O resumo das entregas de hoje e dos atrasos é enviado por e-mail ao grupo de Compras todo dia às 8h. Use
        o contato para falar com o fornecedor antes do vencimento.
      </p>
    </>
  );
};

export default EntregasSection;
